/**
 * F17 T17.11.7 + T17.11.C — tenant API client dla biblioteki szablonów procesów.
 *
 * Woła endpointy tenant-scope (Bearer + X-Tenant-Id przez globalny interceptor):
 *  - GET  /api/process-templates               — lista opublikowanych wzorów
 *  - POST /api/process-templates/:id/copy      — skopiuj wzór do procesów firmy
 *  - POST /api/process-templates/:id/enable    — włącz wzór dla firmy
 *  - POST /api/process-templates/:id/disable   — wyłącz wzór dla firmy
 *
 * Wzorzec per-domena: named async functions, discriminated union dla błędów.
 */

import { api } from '@/lib/api';

// ============================================================================
// Domain types — odpowiedź GET /api/process-templates
// ============================================================================

export interface ProcessTemplateSummary {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  targetModuleCode: string;
  version: number;
  publishedAt: string | null;
  /** T17.11.C: stan włączenia wzoru dla firmy (domyślnie true). */
  isEnabled: boolean;
}

export interface ListProcessTemplatesResponse {
  data: ProcessTemplateSummary[];
}

// ============================================================================
// List (biblioteka wzorów)
// ============================================================================

/** GET /api/process-templates — lista opublikowanych wzorów (tenant scope). */
export async function listProcessTemplates(): Promise<ListProcessTemplatesResponse> {
  const res = await api.get<ListProcessTemplatesResponse>('/process-templates');
  return res.data;
}

// ============================================================================
// Copy (kopiowanie wzoru do procesów firmy)
// ============================================================================

export interface CopyTemplateSuccess {
  ok: true;
  processId: string;
}

export interface CopyTemplateFailure {
  ok: false;
  error:
    | 'TEMPLATE_NOT_FOUND'
    | 'PERMISSION_DENIED'
    | 'INVALID_TEMPLATE_ID'
    | 'TEMPLATE_DISABLED'
    | 'UNKNOWN';
}

/** POST /api/process-templates/:id/copy — tworzy nowy proces z wzoru.
 *  Zwraca processId nowo utworzonego procesu (draft) w tenant DB. */
export async function copyProcessTemplate(
  templateId: string,
): Promise<CopyTemplateSuccess | CopyTemplateFailure> {
  try {
    const res = await api.post<{ processId: string }>(`/process-templates/${templateId}/copy`);
    return { ok: true, processId: res.data.processId };
  } catch (err: unknown) {
    const ax = err as {
      response?: { status?: number; data?: { error?: string } };
    };
    const status = ax.response?.status;
    const data = ax.response?.data;
    if (status === 404 && data?.error === 'TEMPLATE_NOT_FOUND') {
      return { ok: false, error: 'TEMPLATE_NOT_FOUND' };
    }
    if (status === 403 && data?.error === 'PERMISSION_DENIED') {
      return { ok: false, error: 'PERMISSION_DENIED' };
    }
    if (status === 400 && data?.error === 'INVALID_TEMPLATE_ID') {
      return { ok: false, error: 'INVALID_TEMPLATE_ID' };
    }
    if (status === 409 && data?.error === 'TEMPLATE_DISABLED') {
      return { ok: false, error: 'TEMPLATE_DISABLED' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}

// ============================================================================
// Use (użyj wprost — linked live process)
// ============================================================================

export interface UseTemplateSuccess {
  ok: true;
  processId: string;
}

export interface UseTemplateFailure {
  ok: false;
  error:
    | 'TEMPLATE_NOT_FOUND'
    | 'PERMISSION_DENIED'
    | 'INVALID_TEMPLATE_ID'
    | 'TEMPLATE_DISABLED'
    | 'UNKNOWN';
}

/** POST /api/process-templates/:id/use — tworzy nowy powiązany proces (isLinked=true).
 *  Graf jest live-read z szablonu ADM — nie jest kopiowany lokalnie.
 *  Zwraca processId nowo utworzonego procesu w tenant DB. */
export async function applyProcessTemplate(
  templateId: string,
): Promise<UseTemplateSuccess | UseTemplateFailure> {
  try {
    const res = await api.post<{ processId: string }>(`/process-templates/${templateId}/use`);
    return { ok: true, processId: res.data.processId };
  } catch (err: unknown) {
    const ax = err as {
      response?: { status?: number; data?: { error?: string } };
    };
    const status = ax.response?.status;
    const data = ax.response?.data;
    if (status === 404 && data?.error === 'TEMPLATE_NOT_FOUND') {
      return { ok: false, error: 'TEMPLATE_NOT_FOUND' };
    }
    if (status === 403 && data?.error === 'PERMISSION_DENIED') {
      return { ok: false, error: 'PERMISSION_DENIED' };
    }
    if (status === 400 && data?.error === 'INVALID_TEMPLATE_ID') {
      return { ok: false, error: 'INVALID_TEMPLATE_ID' };
    }
    if (status === 409 && data?.error === 'TEMPLATE_DISABLED') {
      return { ok: false, error: 'TEMPLATE_DISABLED' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}

// ============================================================================
// Toggle (włącz / wyłącz wzór dla firmy — T17.11.C)
// ============================================================================

export interface ToggleTemplateSuccess {
  ok: true;
}

export interface ToggleTemplateFailure {
  ok: false;
  error: 'TEMPLATE_NOT_FOUND' | 'PERMISSION_DENIED' | 'INVALID_TEMPLATE_ID' | 'UNKNOWN';
}

async function _toggle(
  templateId: string,
  action: 'enable' | 'disable',
): Promise<ToggleTemplateSuccess | ToggleTemplateFailure> {
  try {
    await api.post(`/process-templates/${templateId}/${action}`);
    return { ok: true };
  } catch (err: unknown) {
    const ax = err as {
      response?: { status?: number; data?: { error?: string } };
    };
    const status = ax.response?.status;
    const data = ax.response?.data;
    if (status === 404 && data?.error === 'TEMPLATE_NOT_FOUND') {
      return { ok: false, error: 'TEMPLATE_NOT_FOUND' };
    }
    if (status === 403 && data?.error === 'PERMISSION_DENIED') {
      return { ok: false, error: 'PERMISSION_DENIED' };
    }
    if (status === 400 && data?.error === 'INVALID_TEMPLATE_ID') {
      return { ok: false, error: 'INVALID_TEMPLATE_ID' };
    }
    return { ok: false, error: 'UNKNOWN' };
  }
}

/** POST /api/process-templates/:id/enable — włącza wzór (isEnabled=true) dla firmy. */
export function enableProcessTemplate(
  templateId: string,
): Promise<ToggleTemplateSuccess | ToggleTemplateFailure> {
  return _toggle(templateId, 'enable');
}

/** POST /api/process-templates/:id/disable — wyłącza wzór (isEnabled=false) dla firmy. */
export function disableProcessTemplate(
  templateId: string,
): Promise<ToggleTemplateSuccess | ToggleTemplateFailure> {
  return _toggle(templateId, 'disable');
}
